import React, { useState } from 'react';

const DaycareCancelModal = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  enrollment,
  apiCall,
  showError,
  showSuccess 
}) => {
  const [cancelReason, setCancelReason] = useState('');
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [cancelFutureCharges, setCancelFutureCharges] = useState(true);
  const [forgiveBalance, setForgiveBalance] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleCancel = async (e) => {
    e.preventDefault();
    
    if (!cancelReason.trim()) {
      showError('Please provide a reason for cancellation');
      return;
    }

    setLoading(true);
    try {
      await apiCall(`/daycare/enrollments/${enrollment.EnrollmentID}/cancel`, {
        method: 'PUT',
        body: JSON.stringify({
          cancelReason,
          endDate,
          cancelFutureCharges,
          forgiveBalance
        })
      });

      showSuccess(`Enrollment cancelled for ${enrollment.FirstName} ${enrollment.LastName}`);
      onSuccess();
      handleClose();
    } catch (error) {
      showError('Error cancelling enrollment: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCancelReason('');
    setEndDate(new Date().toISOString().split('T')[0]);
    setCancelFutureCharges(true);
    setForgiveBalance(false);
    onClose();
  };

  if (!isOpen || !enrollment) return null;

  // TrueBalance = charged - paid from MonthlyCharges. BalanceRemaining is a
  // stale legacy column that reads negative, which would silently hide the
  // forgive-balance workflow on families who genuinely owe money.
  const hasBalance = (enrollment.TrueBalance || 0) > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Cancel Enrollment</h2>
            <p className="text-red-100 text-sm mt-1">
              This action cannot be undone
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Enrollment Info */}
        <div className="px-6 py-4 bg-red-50 border-b border-red-200">
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-gray-600">Child:</span>
              <span className="ml-2 font-semibold text-gray-900">
                {enrollment.FirstName} {enrollment.LastName}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Package:</span>
              <span className="ml-2 font-semibold text-gray-900">
                {enrollment.PackageName}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Current Status:</span>
              <span className="ml-2 font-semibold text-green-600">
                {enrollment.Status}
              </span>
            </div>
            {hasBalance && (
              <div>
                <span className="text-gray-600">Outstanding Balance:</span>
                <span className="ml-2 font-semibold text-red-600">
                  ${enrollment.TrueBalance?.toFixed(2) || '0.00'}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleCancel} className="p-6 space-y-4">
          
          {/* Cancel Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason for Cancellation <span className="text-red-500">*</span>
            </label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g., Moving to different city, Child started school, Family financial issues..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
              required
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Last day of enrollment
            </p>
          </div>

          {/* Cancel Future Charges */}
          <div className="border-t border-gray-200 pt-4">
            <label className="flex items-start">
              <input
                type="checkbox"
                checked={cancelFutureCharges}
                onChange={(e) => setCancelFutureCharges(e.target.checked)}
                className="mt-1 mr-3"
              />
              <div>
                <span className="text-sm font-medium text-gray-700">
                  Cancel future pending charges
                </span>
                <p className="text-xs text-gray-500 mt-1">
                  Remove any unpaid monthly charges dated after the end date
                </p>
              </div>
            </label>
          </div>

          {/* Forgive Balance */}
          {hasBalance && (
            <div className="border-t border-gray-200 pt-4">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={forgiveBalance}
                  onChange={(e) => setForgiveBalance(e.target.checked)}
                  className="mt-1 mr-3"
                />
                <div>
                  <span className="text-sm font-medium text-gray-700">
                    Forgive outstanding balance (${enrollment.TrueBalance?.toFixed(2)})
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    Write off the remaining balance (family won't owe it)
                  </p>
                </div>
              </label>
            </div>
          )}

          {/* Warning Box */}
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-red-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-red-800">
                <p className="font-medium">Warning - This action cannot be undone</p>
                <ul className="mt-1 space-y-1 list-disc list-inside text-xs">
                  <li>Enrollment will be permanently cancelled</li>
                  <li>No more charges will be generated</li>
                  {!forgiveBalance && hasBalance && (
                    <li className="font-semibold">Family will still owe ${enrollment.TrueBalance?.toFixed(2)}</li>
                  )}
                  {forgiveBalance && hasBalance && (
                    <li className="font-semibold text-red-900">Balance of ${enrollment.TrueBalance?.toFixed(2)} will be written off</li>
                  )}
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Keep Enrollment
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Cancelling...' : '❌ Cancel Enrollment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DaycareCancelModal;
